import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Heart, Loader2, Share2, ShieldCheck, MapPin,
  BedDouble, Bath, Home as HomeIcon, Wifi, MessageCircle,
  GraduationCap, Clock, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  PLACE_TYPE_LABELS, AMENITY_LABELS, AMENITY_CATEGORIES,
} from '../lib/listingConstants';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMonths = (m) => {
  if (!m) return '—';
  if (m === 12) return '12 months';
  return `${m} months`;
};

const urlForPhoto = (p) => (p?.signed_token ? `${API}/api/files/${p.signed_token}` : null);

const PhotoGallery = ({ photos, onOpenLightbox }) => {
  const [primary, ...rest] = photos;
  const side = rest.slice(0, 4);
  return (
    <div className="relative" data-testid="listing-photo-gallery">
      {/* Mobile: single hero + swipe (naive: horizontal scroll). */}
      <div className="md:hidden">
        <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
          {primary && <img src={urlForPhoto(primary)} alt={primary.caption || ''} className="w-full h-full object-cover" />}
        </div>
        {photos.length > 1 && (
          <div className="flex gap-1 px-4 py-2 overflow-x-auto scrollbar-thin">
            {photos.slice(0, 8).map((p) => (
              <button
                key={p.file_id}
                type="button"
                onClick={() => onOpenLightbox(photos.indexOf(p))}
                className="h-16 w-20 flex-shrink-0 rounded overflow-hidden border border-slate-200"
                data-testid={`mobile-thumb-${p.file_id}`}
              >
                <img src={urlForPhoto(p)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Desktop: 1+4 grid */}
      <div className="hidden md:grid grid-cols-4 gap-2 aspect-[16/9] rounded-2xl overflow-hidden">
        <div className="col-span-2 row-span-2 relative bg-slate-100">
          {primary && <img src={urlForPhoto(primary)} alt={primary.caption || ''} className="w-full h-full object-cover" />}
        </div>
        {side.map((p) => (
          <div key={p.file_id} className="relative bg-slate-100">
            <img src={urlForPhoto(p)} alt={p.caption || ''} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
      {photos.length > 5 && (
        <button
          type="button"
          onClick={() => onOpenLightbox(0)}
          className="absolute bottom-4 right-4 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-semibold shadow-soft hover:shadow-hover"
          data-testid="show-all-photos"
        >
          Show all {photos.length} photos
        </button>
      )}
    </div>
  );
};

const Lightbox = ({ photos, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex || 0);
  if (idx >= photos.length) return null;
  const p = photos[idx];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 bg-black text-white" data-testid="photo-lightbox">
        <div className="relative">
          <img src={urlForPhoto(p)} alt={p.caption || ''} className="w-full max-h-[85vh] object-contain" />
          <button onClick={onClose} className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 flex items-center justify-between text-xs">
          <span>{idx + 1} / {photos.length}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>Previous</Button>
            <Button size="sm" variant="secondary" disabled={idx === photos.length - 1} onClick={() => setIdx(idx + 1)}>Next</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BookingCard = ({ listing, onMessage, saved, onToggleSaved, variant = 'desktop' }) => {
  const [open, setOpen] = useState(false);
  const total = listing.total_monthly_cost_cad ?? 0;

  const Breakdown = () => (
    <div className="text-xs text-slate-600 space-y-1.5 pt-3 border-t border-slate-100 mt-3" data-testid="cost-breakdown">
      <div className="flex justify-between"><span>Rent</span><span>${(listing.monthly_rent_cad ?? 0).toLocaleString()}</span></div>
      {!listing.includes_utilities && (
        <div className="flex justify-between">
          <span>Utilities</span>
          <span>${(listing.additional_utility_cost_cad ?? 0).toLocaleString()}</span>
        </div>
      )}
      {!listing.includes_internet && (
        <div className="flex justify-between">
          <span>Internet</span>
          <span>${(listing.internet_cost_if_separate_cad ?? 0).toLocaleString()}</span>
        </div>
      )}
      {!listing.includes_parking && listing.parking_type !== 'none' && (
        <div className="flex justify-between">
          <span>Parking</span>
          <span>${(listing.parking_cost_if_separate_cad ?? 0).toLocaleString()}</span>
        </div>
      )}
      {(listing.other_fees || []).map((f, i) => (
        <div key={i} className="flex justify-between">
          <span>{f.name || 'Fee'}</span>
          <span>${(f.amount_cad ?? 0).toLocaleString()}</span>
        </div>
      ))}
      <div className="flex justify-between pt-2 border-t border-slate-100 font-bold text-slate-900">
        <span>Total per month</span>
        <span>${total.toLocaleString()}</span>
      </div>
    </div>
  );

  return (
    <Card className="bg-white border-slate-200 shadow-soft" data-testid={`booking-card-${variant}`}>
      <CardContent className="p-5 space-y-2">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-heading font-extrabold text-slate-900" data-testid="detail-total-price">
            ${total.toLocaleString()}
          </span>
          <span className="text-sm text-slate-500">/mo</span>
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 bg-amber-500 text-white rounded-full">
            ALL-IN
          </span>
        </div>
        <p className="text-xs text-slate-500">Rent + every extra. No hidden fees.</p>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs font-medium text-amber-700 hover:underline inline-flex items-center gap-1"
          data-testid="toggle-breakdown"
        >
          {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          See cost breakdown
        </button>
        {open && <Breakdown />}

        <div className="pt-3 text-xs text-slate-600">
          <div className="flex justify-between py-1">
            <span>Lease length</span>
            <span className="font-medium text-slate-900">{fmtMonths(listing.lease_length_months)}</span>
          </div>
          <div className="flex justify-between py-1">
            <span>Earliest move-in</span>
            <span className="font-medium text-slate-900">{listing.earliest_move_in_date || '—'}</span>
          </div>
        </div>

        <div className="pt-3 space-y-2">
          <Button
            className="w-full h-11 btn-primary"
            disabled
            title="Coming soon"
            data-testid="apply-btn"
          >
            Apply for this place (coming soon)
          </Button>
          <Button
            variant="outline"
            className="w-full h-11 border-slate-300 hover:border-amber-400"
            onClick={onMessage}
            data-testid="message-landlord-btn"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Message landlord
          </Button>
          <button
            type="button"
            onClick={onToggleSaved}
            className={`w-full h-10 text-xs font-medium inline-flex items-center justify-center gap-1.5 rounded-xl border ${
              saved ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-slate-200 text-slate-700 hover:border-amber-300'
            }`}
            data-testid="save-toggle-btn"
          >
            <Heart className={`h-3.5 w-3.5 ${saved ? 'fill-current' : ''}`} />
            {saved ? 'Saved' : 'Save to list'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

const ListingDetail = () => {
  const { listing_id } = useParams();
  const { api, user } = useAuth();
  const { bundle } = useProfile();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [showDescMore, setShowDescMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/listings/${listing_id}`);
        if (cancelled) return;
        setData(res.data);
      } catch (e) {
        if (cancelled) return;
        setErr(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, listing_id]);

  const toggleSaved = async () => {
    if (!data) return;
    try {
      if (data.listing.saved_by_me) {
        await api.delete(`/saved-listings/${data.listing.listing_id}`);
        setData({ ...data, listing: { ...data.listing, saved_by_me: false } });
        toast('Removed from your saved list.');
      } else {
        await api.post('/saved-listings', { listing_id: data.listing.listing_id });
        setData({ ...data, listing: { ...data.listing, saved_by_me: true } });
        toast.success('Saved to your list.');
      }
    } catch (e) {
      toast.error("Couldn't update your saved list.");
    }
  };

  const messageLandlord = async () => {
    if (!data) return;
    try {
      const res = await api.post('/conversations', {
        recipient_user_id: data.landlord.user_id,
        listing_id: data.listing.listing_id,
        initial_message: { content: `Hi ${data.landlord.name}, I'm interested in "${data.listing.title}" — is it still available?` },
      });
      toast.success('Message sent — landlord will see it in their inbox.');
      navigate('/messages');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === 'trust_score_gate') {
        toast.error(`Verify yourself to message landlords. Your Trust Score is ${detail.current_score} — need ${detail.required}.`);
        navigate('/trust-score');
      } else {
        toast.error(typeof detail === 'string' ? detail : "Couldn't send that message.");
      }
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied.');
    } catch (_) {
      toast('Copy failed — try selecting the URL manually.');
    }
  };

  const visibleAmenities = useMemo(() => {
    if (!data) return [];
    const all = data.listing.amenities || [];
    return showAllAmenities ? all : all.slice(0, 8);
  }, [data, showAllAmenities]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader />
        <main className="flex-1 flex items-center justify-center px-5 py-20">
          <div className="text-center max-w-sm">
            <HomeIcon className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <h1 className="text-xl font-heading font-bold text-slate-900">We couldn't find that home.</h1>
            <p className="mt-2 text-sm text-slate-600">It may have been taken down or is still being reviewed.</p>
            <Button className="mt-5 btn-primary" onClick={() => navigate('/search')}>Browse all homes</Button>
          </div>
        </main>
      </div>
    );
  }

  const l = data.listing;
  const landlord = data.landlord;
  const mySchool = bundle?.user?.school_place_id;
  const nearbySchools = [...(l.nearby_schools || [])].sort((a, b) => {
    if (a.school_place_id === mySchool) return -1;
    if (b.school_place_id === mySchool) return 1;
    return (a.distance_meters || 0) - (b.distance_meters || 0);
  });

  return (
    <div className="min-h-screen bg-background pb-32 md:pb-16" data-testid="listing-detail">
      <AppHeader />

      <main className="max-w-6xl mx-auto px-0 md:px-6 py-0 md:py-6">
        {/* Back + top actions (mobile only) */}
        <div className="md:hidden flex items-center justify-between px-4 py-3">
          <button type="button" onClick={() => navigate(-1)} className="p-2 -ml-2 text-slate-700" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-1">
            <button type="button" onClick={copyShare} className="p-2 text-slate-700" data-testid="share-btn">
              <Share2 className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={toggleSaved}
              className={`p-2 ${l.saved_by_me ? 'text-rose-600' : 'text-slate-700'}`}
              data-testid="save-top-btn"
            >
              <Heart className={`h-5 w-5 ${l.saved_by_me ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>

        {/* Photo gallery */}
        <PhotoGallery photos={l.photos || []} onOpenLightbox={setLightboxIdx} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-4 md:px-0 pt-4 md:pt-6">
          {/* Main column */}
          <div className="md:col-span-2 space-y-6">
            {/* Title + specs */}
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900 leading-tight" data-testid="detail-title">
                {l.title}
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                {PLACE_TYPE_LABELS[l.place_type]} in {l.neighborhood ? `${l.neighborhood}, ` : ''}{l.city}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-700">
                <span className="inline-flex items-center gap-1"><BedDouble className="h-4 w-4" /> {l.bedroom_count} bed</span>
                <span className="inline-flex items-center gap-1"><Bath className="h-4 w-4" /> {l.bathroom_count} {l.bathroom_type} bath</span>
                <span className="inline-flex items-center gap-1"><HomeIcon className="h-4 w-4" /> {l.furnished}</span>
              </div>
            </div>

            {/* Trust signal */}
            <Card className="bg-emerald-50 border-emerald-200/60" data-testid="trust-signal">
              <CardContent className="p-4 flex gap-3 items-start">
                <ShieldCheck className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Verified landlord</p>
                  <p className="text-xs text-emerald-800 mt-0.5 leading-relaxed">
                    Identity and ownership confirmed by Sturent before this listing went live.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Top features */}
            {(l.top_features || []).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="top-features">
                {l.top_features.slice(0, 3).map((f, i) => (
                  <Card key={i} className="bg-white border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-sm font-medium text-slate-900 leading-snug">{f}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Description */}
            {l.description_long && (
              <section data-testid="description-section">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">About this home</h2>
                <p className={`text-sm text-slate-700 leading-relaxed whitespace-pre-wrap ${showDescMore ? '' : 'line-clamp-6'}`}>
                  {l.description_long}
                </p>
                {l.description_long.length > 300 && (
                  <button
                    type="button"
                    onClick={() => setShowDescMore((v) => !v)}
                    className="mt-2 text-sm font-medium text-amber-700 underline"
                    data-testid="show-more-desc"
                  >
                    {showDescMore ? 'Show less' : 'Show more'}
                  </button>
                )}
              </section>
            )}

            {/* Nearby schools */}
            {nearbySchools.length > 0 && (
              <section data-testid="nearby-schools">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">Get to school</h2>
                <div className="space-y-2">
                  {nearbySchools.slice(0, 5).map((s) => (
                    <div
                      key={s.school_place_id}
                      className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${
                        s.school_place_id === mySchool ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'
                      }`}
                      data-testid={`school-${s.school_place_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <GraduationCap className="h-4 w-4 text-slate-500 flex-shrink-0" />
                        <span className="text-sm text-slate-900 truncate">{s.school_name}</span>
                      </div>
                      <div className="text-xs text-slate-600 text-right flex-shrink-0">
                        <div>~{s.transit_minutes} min transit</div>
                        <div className="text-slate-400">{(s.distance_meters / 1000).toFixed(1)} km</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Amenities */}
            {(l.amenities || []).length > 0 && (
              <section data-testid="amenities-section">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">What this place offers</h2>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {visibleAmenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-slate-700">
                      <Wifi className="h-3.5 w-3.5 text-slate-400" />
                      {AMENITY_LABELS[a] || a}
                    </div>
                  ))}
                </div>
                {(l.amenities || []).length > 8 && (
                  <button
                    type="button"
                    onClick={() => setShowAllAmenities((v) => !v)}
                    className="mt-3 text-sm font-medium text-amber-700 underline"
                    data-testid="show-all-amenities"
                  >
                    {showAllAmenities ? 'Show less' : `Show all ${l.amenities.length} amenities`}
                  </button>
                )}
              </section>
            )}

            {/* Where you'll sleep */}
            {(l.photos || []).some((p) => p.room_label === 'Bedroom') && (
              <section data-testid="where-youll-sleep">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">Where you'll sleep</h2>
                <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-2">
                  {(l.photos || []).filter((p) => p.room_label === 'Bedroom').map((p) => (
                    <div key={p.file_id} className="flex-shrink-0 w-52 rounded-xl overflow-hidden border border-slate-200">
                      <div className="aspect-[4/3] bg-slate-100">
                        <img src={urlForPhoto(p)} alt={p.caption || ''} className="w-full h-full object-cover" />
                      </div>
                      {p.caption && <p className="p-2 text-xs text-slate-700">{p.caption}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Roommate situation */}
            {l.shared_with_others && (
              <section className="p-4 border border-slate-200 rounded-2xl bg-white" data-testid="roommate-situation">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-1">Who else lives here</h2>
                <p className="text-sm text-slate-700">
                  {l.shared_with_count} other tenant{l.shared_with_count === 1 ? '' : 's'}.{' '}
                  {l.shared_with_description || 'More details shared after your application is accepted.'}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  For their privacy, names and profiles are only shared after the landlord accepts your application.
                </p>
              </section>
            )}

            {/* House rules */}
            <section data-testid="house-rules">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">House rules</h2>
              <div className="grid grid-cols-3 gap-3">
                <Card className="bg-white border-slate-200"><CardContent className="p-3 text-xs">
                  <p className="font-semibold text-slate-900">Pets</p>
                  <p className="mt-0.5 text-slate-600 capitalize">{(l.pets_allowed || '').replace(/_/g, ' ')}</p>
                </CardContent></Card>
                <Card className="bg-white border-slate-200"><CardContent className="p-3 text-xs">
                  <p className="font-semibold text-slate-900">Smoking</p>
                  <p className="mt-0.5 text-slate-600 capitalize">{(l.smoking_allowed || '').replace(/_/g, ' ')}</p>
                </CardContent></Card>
                <Card className="bg-white border-slate-200"><CardContent className="p-3 text-xs">
                  <p className="font-semibold text-slate-900">Guests</p>
                  <p className="mt-0.5 text-slate-600 capitalize">{(l.guests_allowed || '').replace(/_/g, ' ')}</p>
                </CardContent></Card>
              </div>
              {l.additional_house_rules && (
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{l.additional_house_rules}</p>
              )}
            </section>

            {/* Lease terms */}
            <section data-testid="lease-terms">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">Lease terms</h2>
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4 text-sm text-slate-700 space-y-1.5">
                  <div className="flex justify-between"><span>Lease type</span><span className="font-medium text-slate-900">{(l.lease_type || '').replace('_', '-') || '—'}</span></div>
                  <div className="flex justify-between"><span>Lease length</span><span className="font-medium text-slate-900">{fmtMonths(l.lease_length_months)}</span></div>
                  <div className="flex justify-between"><span>Earliest move-in</span><span className="font-medium text-slate-900">{l.earliest_move_in_date || '—'}</span></div>
                  <div className="flex justify-between"><span>Last month's rent</span><span className="font-medium text-slate-900">{l.last_month_rent_required ? 'Required' : 'Not required'}</span></div>
                  <div className="flex justify-between"><span>Ontario Standard Lease</span><span className="font-medium text-slate-900">{l.ontario_standard_lease_will_be_used ? 'Yes' : 'No'}</span></div>
                </CardContent>
              </Card>
              {l.lease_break_policy && (
                <p className="mt-3 text-sm text-slate-700">{l.lease_break_policy}</p>
              )}
            </section>

            {/* Approximate location map */}
            <section data-testid="location-map">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">Approximate location</h2>
              <div className="relative h-48 bg-slate-100 rounded-xl overflow-hidden flex items-center justify-center border border-slate-200">
                <MapPin className="h-8 w-8 text-amber-600" />
                <p className="absolute bottom-2 left-2 right-2 text-center text-xs text-slate-700">
                  {l.neighborhood ? `${l.neighborhood}, ` : ''}{l.city}, Ontario
                </p>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Exact address revealed after a lease is signed. The circle is ~500 m wide.
              </p>
            </section>

            {/* Landlord card */}
            <Card className="bg-white border-slate-200" data-testid="landlord-card">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold">
                  {landlord?.name?.charAt(0) || 'L'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">Hosted by {landlord?.name}</p>
                  {landlord?.identity_verified && (
                    <p className="text-xs text-emerald-700 inline-flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" /> Identity verified
                    </p>
                  )}
                </div>
                <Button size="sm" variant="outline" className="border-slate-300" onClick={messageLandlord} data-testid="landlord-card-message">
                  <MessageCircle className="h-3.5 w-3.5 mr-1" /> Message
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right column — sticky booking card */}
          <aside className="hidden md:block">
            <div className="sticky top-24">
              <BookingCard
                listing={l}
                onMessage={messageLandlord}
                saved={l.saved_by_me}
                onToggleSaved={toggleSaved}
                variant="desktop"
              />
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile sticky price bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center gap-3 z-30 shadow-lg" data-testid="mobile-price-bar">
        <div>
          <div className="text-lg font-heading font-extrabold text-slate-900">${(l.total_monthly_cost_cad ?? 0).toLocaleString()}<span className="text-xs font-normal text-slate-500">/mo</span></div>
          <div className="text-[10px] text-amber-700 font-semibold">All-in</div>
        </div>
        <Button
          className="ml-auto h-11 px-4"
          variant="outline"
          onClick={messageLandlord}
          data-testid="mobile-message-btn"
        >
          <MessageCircle className="h-4 w-4 mr-1.5" /> Message
        </Button>
        <Button className="h-11 px-4 btn-primary" disabled title="Coming soon" data-testid="mobile-apply-btn">
          Apply
        </Button>
      </div>

      {lightboxIdx !== null && (
        <Lightbox photos={l.photos} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </div>
  );
};

export default ListingDetail;
